import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { APP_NAME } from '@/config/app';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { useConsent } from '@/consent';

/**
 * First-visit, opt-in consent banner. Non-modal sticky bar at the bottom of the
 * viewport (no vaul snap points — those translate auto-height sheets off-screen).
 * Shown only while the user has not yet decided. "Customize" expands per-category
 * checkboxes; Accept/Reject act on both categories at once.
 *
 * The privacy link is a plain <a> (full navigation), not a TanStack <Link>: the
 * policy page is public and rarely clicked, and this keeps the banner free of
 * router context (simpler, hazard-free unit tests).
 */
export function ConsentBanner() {
    const { t } = useTranslation();
    const { consent, acceptAll, rejectAll, setCategories } = useConsent();
    const [customizing, setCustomizing] = useState(false);
    // Initialize from stored consent (false while undecided) — never pre-tick.
    // Pre-checked boxes are not valid consent under GDPR (CJEU C-673/17 Planet49).
    const [analytics, setAnalytics] = useState(consent.analytics);
    const [sessionReplay, setSessionReplay] = useState(consent.sessionReplay);
    const [errorMonitoring, setErrorMonitoring] = useState(consent.errorMonitoring);

    if (consent.decided) return null;

    return (
        <div
            role="region"
            aria-labelledby="consent-banner-heading"
            className="fixed bottom-4 left-1/2 z-[100] w-[80%] max-w-2xl -translate-x-1/2 rounded-lg border border-border bg-background/95 p-4 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-background/80"
        >
            <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-1">
                    <h2 id="consent-banner-heading" className="text-sm font-semibold">{t('consent.banner.title')}</h2>
                    <p className="text-sm text-muted-foreground">
                        {t('consent.banner.description', { appName: APP_NAME })}
                    </p>
                    <a href="/privacy" className="text-sm font-medium underline underline-offset-4">
                        {t('consent.banner.privacyLink')}
                    </a>
                </div>

                {customizing && (
                    <div className="flex flex-col gap-3 rounded-md border border-border p-3">
                        <label className="flex items-start gap-3">
                            <Checkbox
                                checked={analytics}
                                onCheckedChange={(v) => setAnalytics(v === true)}
                                aria-label={t('consent.categories.analytics.label')}
                            />
                            <span className="flex flex-col gap-0.5">
                                <span className="text-sm font-medium">{t('consent.categories.analytics.label')}</span>
                                <span className="text-xs text-muted-foreground">{t('consent.categories.analytics.description')}</span>
                            </span>
                        </label>
                        <label className="flex items-start gap-3">
                            <Checkbox
                                checked={sessionReplay}
                                onCheckedChange={(v) => setSessionReplay(v === true)}
                                aria-label={t('consent.categories.sessionReplay.label')}
                            />
                            <span className="flex flex-col gap-0.5">
                                <span className="text-sm font-medium">{t('consent.categories.sessionReplay.label')}</span>
                                <span className="text-xs text-muted-foreground">{t('consent.categories.sessionReplay.description')}</span>
                            </span>
                        </label>
                        <label className="flex items-start gap-3">
                            <Checkbox
                                checked={errorMonitoring}
                                onCheckedChange={(v) => setErrorMonitoring(v === true)}
                                aria-label={t('consent.categories.errorMonitoring.label')}
                            />
                            <span className="flex flex-col gap-0.5">
                                <span className="text-sm font-medium">{t('consent.categories.errorMonitoring.label')}</span>
                                <span className="text-xs text-muted-foreground">{t('consent.categories.errorMonitoring.description')}</span>
                            </span>
                        </label>
                    </div>
                )}

                <div className="flex flex-wrap justify-end gap-2">
                    {customizing ? (
                        <Button size="sm" onClick={() => setCategories({ analytics, sessionReplay, errorMonitoring })}>
                            {t('consent.banner.save')}
                        </Button>
                    ) : (
                        <Button variant="outline" size="sm" onClick={() => setCustomizing(true)}>
                            {t('consent.banner.customize')}
                        </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={rejectAll}>
                        {t('consent.banner.rejectAll')}
                    </Button>
                    <Button size="sm" onClick={acceptAll}>
                        {t('consent.banner.acceptAll')}
                    </Button>
                </div>
            </div>
        </div>
    );
}
